// ============================================================================
// Privacy — the /privacy page, plus the three headline facts the pricing
// page repeats (PrivacyFacts). Everything here describes what the code
// does today; when one of these behaviours changes, this page changes with
// it. The engineering detail behind each claim:
//
//  - IP addresses: server/src/logs/visitor.ts (chat visitors — a keyed,
//    per-bot hash of IP + browser) and server/src/http/routes/auth.ts
//    (sign-in rate limiting — a keyed hash). No raw IP is written anywhere.
//  - Verified domains: server/src/bots/verification.ts and
//    server/src/http/routes/session.ts.
//  - Retention: the per-bot log_retention_days setting (1–365, default 90)
//    and the daily sweep in server/src/index.ts.
//  - Model provider: LLM_PROVIDER in server/wrangler.jsonc (OpenAI).
//
// PRIVACY.md at the repo root is the longer, technical version.

const UPDATED = '13 October 2026';
const CONTACT_EMAIL = 'hello@volgachat.com';

const FACTS = [
  {
    title: 'No IP addresses stored',
    body: "Your visitors' IP addresses are never stored. To group one visitor's messages together we keep only a one-way code made from the address and browser with a secret key, different for every website, so it can't be used to follow anyone across sites.",
  },
  {
    title: 'Verified domains only',
    body: "Your assistant only runs on websites you've proven you own. A copied snippet on anyone else's site gets nothing, so nobody can put your assistant, or your message allowance, on their pages.",
  },
  {
    title: 'You decide how long chats are kept',
    body: 'Conversations are kept for 90 days unless you choose otherwise: anything from 1 day to a year, per chatbot. Older ones are deleted automatically every day, and you can delete any conversation at any time.',
  },
];

/** The three headline facts, as cards. Used on this page and under the
 * pricing cards. */
export function PrivacyFacts() {
  return (
    <div class="pv-facts">
      {FACTS.map((f) => (
        <div class="pv-fact" key={f.title}>
          <h3>{f.title}</h3>
          <p>{f.body}</p>
        </div>
      ))}
    </div>
  );
}

export function Privacy() {
  return (
    <section class="site-section pv-page">
      <div class="site-wrap pv-wrap">
        <span class="site-eyebrow">Privacy</span>
        <h1>Privacy policy</h1>
        <p class="pv-updated">Last updated {UPDATED}</p>
        <p class="site-section-lead pv-lead">
          VolgaChat puts an AI assistant on other people's websites. This page covers both groups whose information we handle:{' '}
          <strong>customers</strong>, who sign up and run a chatbot, and <strong>visitors</strong>, who chat with one on a customer's
          site.
        </p>

        <PrivacyFacts />

        <div class="pv-body">
          <h2>What we keep from a chat</h2>
          <ul>
            <li>
              <strong>The messages</strong> — what the visitor wrote and what the assistant answered, so the website owner can read
              them in their dashboard.
            </li>
            <li>
              <strong>The page the chat happened on</strong> — the address only, without anything after a "?" or "#". That part is
              removed in the visitor's browser before it's sent, because it often holds order numbers or email addresses.
            </li>
            <li>
              <strong>A visitor code</strong> — the one-way code described above. It tells the website owner that several messages came
              from the same person; it doesn't say who that person is.
            </li>
            <li>
              <strong>Contact details, only if the visitor gives them</strong> — some assistants ask for a name, email or phone number
              in a form, for a quote or a callback. Nothing is collected unless the visitor fills it in. What they enter is emailed
              to the website owner straight away, and to up to three other addresses the owner chooses — each of which has to confirm
              before it receives anything, and gets the contact details only, never the conversation or dashboard access. It's
              listed in the owner's dashboard, and our copy is deleted along with the conversation.
            </li>
          </ul>

          <h2>What we don't keep</h2>
          <ul>
            <li>Visitors' IP addresses. Our code never saves them, in our database or anywhere else.</li>
            <li>Cookies — the chat sets none.</li>
            <li>Visitor accounts or profiles. Visitors never sign up for anything.</li>
            <li>Anything that links one person's chats on different websites.</li>
            <li>Advertising or analytics trackers. There are none in the chat.</li>
          </ul>

          <h2>What stays in the visitor's browser</h2>
          <p>
            The current conversation is kept in the browser tab, so it survives moving between pages, and is cleared when the tab is
            closed. If a visitor moves or shrinks the chat bubble, that choice is remembered on that website. Neither is sent to us.
          </p>

          <h2>How long conversations are kept</h2>
          <p>
            90 days by default. Each website owner can change this for each of their chatbots, from 1 day up to 365 days, and every
            day we delete the conversations that have passed their chatbot's limit. Owners can also delete a single conversation, all
            of a chatbot's conversations, or the whole chatbot at any time, and the deletion takes effect straight away.
          </p>

          <h2>Who can read conversations</h2>
          <p>
            The owner of the chatbot, in their own dashboard, for their own chatbots only. Our own admin tools show counts — how many
            conversations, how many errors — and never message content.
          </p>

          <h2>Where chatbots can run</h2>
          <p>
            Before a chatbot answers anyone, its owner has to prove they control the website: by placing the chatbot's snippet, a
            verification tag or a verification file on it. Requests from any other website are refused. That keeps a customer's
            assistant, and the content it has learned, on their own sites.
          </p>

          <h2>Services we use</h2>
          <ul>
            <li>
              <strong>Cloudflare</strong> runs our servers and database, and sends the emails that tell website owners about new
              contacts. Like any web host, it sees visitors' IP addresses in order to deliver traffic.
            </li>
            <li>
              <strong>OpenAI</strong> writes the assistant's answers. It receives the conversation and the parts of the website owner's
              content needed to answer. OpenAI doesn't use what it receives through its API to train its models, and keeps it for up to
              30 days to check for abuse before deleting it.
            </li>
            <li>
              <strong>Google</strong> handles sign-in for customers. We receive their name and email address.
            </li>
            <li>
              <strong>Dodo Payments</strong> takes customers' payments. Card details go to Dodo Payments, never to us.
            </li>
          </ul>

          {/* Facts as deployed: D1 chatframe-auth-production runs in APAC with
              no jurisdiction and no read replicas (`wrangler d1 info`); KV
              (sign-in sessions, uploaded chat-bubble animations) is cached
              worldwide; OpenAI's API processes in the US. Update this if any
              of those change. */}
          <h2>Where data is kept</h2>
          <p>
            Conversations, contact details and chatbot content are stored in our database on Cloudflare, in its Asia-Pacific region.
            Sign-in sessions and uploaded chat-bubble animations are also cached across Cloudflare's worldwide network, and each
            request is handled at the Cloudflare data centre nearest the visitor. OpenAI processes conversations in the United States.
            We can't yet keep data in a region you choose.
          </p>

          <h2>Customers' own information</h2>
          <p>
            When you sign up we keep the name and email address Google gives us, your plan and your usage, and the content you add for
            your chatbots. To stop people guessing at sign-ins, we count recent sign-in attempts against a one-way code of the address
            they came from — keyed with a secret, like the visitor code, and never the address itself.
          </p>

          <h2>Questions and deletion requests</h2>
          <p>
            If you chatted with an assistant on a website and want your conversation deleted, the quickest route is the website's owner,
            who can delete it from their dashboard immediately. You can also email us at{' '}
            <a href={`mailto:${CONTACT_EMAIL}?subject=${encodeURIComponent('Privacy request')}`}>{CONTACT_EMAIL}</a> with roughly when the
            chat happened, on which website, and what was said, and we'll find it and delete it. Customers can email the same address
            about anything on this page.
          </p>

          <h2>Changes to this page</h2>
          <p>
            When what we collect or how long we keep it changes, this page changes too, and the date at the top is updated.
          </p>
        </div>
      </div>
    </section>
  );
}
